import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HandCoins, Wallet, Clock, FileCheck2, Paperclip } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { formatINR } from '@/shared/utils/currency';
import { cn } from '@/shared/utils/utils';

interface HandoffRow {
  _id: string;
  orderNumber: string;
  customerName: string;
  orderTotal: number;
  amountPaid?: number;
  handoff: {
    status?: string;
    paymentArrangement?: string;
    /** Terms / method / cheque / reference as chosen at hand-off (newer hand-offs). */
    paymentDetails?: { paymentTermsLabel?: string; creditDays?: number | null; paymentMethodLabel?: string; paymentMethodOther?: string; chequeNo?: string; chequeBank?: string; chequeDate?: string; paymentReference?: string } | null;
    amountReceived?: number;
    balanceAmount?: number;
    paymentProofs?: { url: string; name: string }[];
  };
}

/** What Sales told Accounts about each order's payment when handing it to Dispatch. */
export function HandoffPaymentReview() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['accounts', 'handoff-payment-review'],
    queryFn: async () => (await api.get('/api/sales-ops/orders/handoff-payment-review')).data.data as HandoffRow[],
  });

  const totals = useMemo(() => {
    const balance = data.reduce((s, r) => s + (Number(r.handoff.balanceAmount) || 0), 0);
    const received = data.reduce((s, r) => s + (Number(r.handoff.amountReceived) || 0), 0);
    const withBalance = data.filter((r) => Number(r.handoff.balanceAmount) > 0).length;
    const withoutProof = data.filter((r) => !r.handoff.paymentProofs?.length).length;
    return { balance, received, withBalance, withoutProof };
  }, [data]);

  if (isLoading) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
      <PageHeader
        icon={HandCoins}
        eyebrow="Accounts"
        title="Sales Hand-offs"
        description="Payment terms, amount received, balance and proof that Sales supplied when handing each order to Dispatch."
      />

      <StatGrid cols={4}>
        <StatCard label="Hand-offs" value={data.length} hint="Awaiting or already dispatched" icon={FileCheck2} tone="primary" />
        <StatCard label="Received" value={formatINR(totals.received)} hint="Collected at hand-off" icon={Wallet} tone="success" />
        <StatCard label="Balance to Collect" value={formatINR(totals.balance)} hint={`${totals.withBalance} order${totals.withBalance === 1 ? '' : 's'} with a balance`} icon={Clock} tone="warning" />
        <StatCard label="No Proof Attached" value={totals.withoutProof} hint="Credit orders or proof still missing" icon={Paperclip} tone="neutral" />
      </StatGrid>

      <div className="overflow-hidden rounded-card border border-outline bg-surface shadow-surface-1">
        {data.length === 0 ? (
          <EmptyState title="No hand-offs yet" description="Orders appear here as soon as Sales hands them to Dispatch." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-body-md">
              <thead className="bg-surface-container-low text-label-caps uppercase text-on-surface-subtle">
                <tr>
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Arrangement</th>
                  <th className="px-4 py-3">Dispatch</th>
                  <th className="px-4 py-3 text-right">Received</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3">Proof</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline">
                {data.map((row) => {
                  const balance = Number(row.handoff.balanceAmount) || 0;
                  const dispatched = row.handoff.status === 'dispatched';
                  return (
                    <tr key={row._id} className="transition-colors hover:bg-surface-container-low">
                      <td className="px-4 py-3 font-semibold text-on-surface">{row.orderNumber || row._id}</td>
                      <td className="px-4 py-3 text-on-surface">{row.customerName || '—'}</td>
                      <td className="px-4 py-3 text-on-surface-variant">
                        {row.handoff.paymentArrangement || '—'}
                        {row.handoff.paymentDetails && (
                          <span className="block text-[11px]">
                            {[
                              row.handoff.paymentDetails.paymentTermsLabel,
                              row.handoff.paymentDetails.chequeNo && `Cheque ${row.handoff.paymentDetails.chequeNo}${row.handoff.paymentDetails.chequeBank ? ` (${row.handoff.paymentDetails.chequeBank})` : ''}${row.handoff.paymentDetails.chequeDate ? ` · ${row.handoff.paymentDetails.chequeDate}` : ''}`,
                              row.handoff.paymentDetails.paymentReference && `Ref ${row.handoff.paymentDetails.paymentReference}`,
                            ].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'inline-flex rounded-full px-2 py-0.5 text-body-xs font-semibold',
                          dispatched ? 'bg-success/10 text-success-text' : 'bg-info/10 text-info-text',
                        )}>
                          {dispatched ? 'Dispatched' : 'Awaiting dispatch'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-on-surface">{formatINR(Number(row.handoff.amountReceived) || 0)}</td>
                      <td className={cn('px-4 py-3 text-right tabular-nums font-semibold', balance > 0 ? 'text-warning-text' : 'text-on-surface-variant')}>
                        {formatINR(balance)}
                      </td>
                      <td className="px-4 py-3">
                        {row.handoff.paymentProofs?.length
                          ? row.handoff.paymentProofs.map((f) => (
                              <a key={f.url} className="mr-2 text-primary underline-offset-2 hover:underline" href={f.url} target="_blank" rel="noreferrer">{f.name}</a>
                            ))
                          : <span className="text-on-surface-subtle">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
